/// Combo / streak bonuses awarded on top of per-aircraft scoring.
///
/// Three combos:
///   - Triple Arrival : 3 successful landings within COMBO_WINDOW_SEC
///   - Departure Rush : 3 successful departures within the same window
///                      (a SID-completion or a `takeoff`-goal exit)
///   - Empty Radar    : transition from ≥1 active aircraft on radar
///                      to 0 active, mid-level (more spawns to come)
///
/// All times are sim-seconds. The tracker is fed by `GameSession`
/// from `recordLanding`, `recordExit`, and `handleFlightPlanCompletion`,
/// and polled each tick for the empty-radar edge.
///
/// "Active aircraft" excludes:
///   - awaitingTakeoff: holding short, doesn't visually clutter
///   - landed / completed: the point of the bonus is that the
///     scope is empty after you cleared everyone
///   - status: takingOff included (still on the runway), since
///     they're consuming attention
///
/// Once a combo fires, the contributing entries are popped from
/// the recent list so the next combo needs three fresh successes.
/// This avoids "rolling triples" where one extra arrival 5 minutes
/// after a triple instantly fires another combo.

const COMBO_WINDOW_SEC = 60

export const TRIPLE_ARRIVAL_BONUS = 150
export const DEPARTURE_RUSH_BONUS = 100
export const EMPTY_RADAR_BONUS = 75

export type ComboKind = 'tripleArrival' | 'departureRush' | 'emptyRadar'

export interface ComboEvent {
  kind: ComboKind
  /// Pretty description for the score event line.
  description: string
  points: number
}

export class ComboTracker {
  /// sim-seconds for each successful landing/departure recorded.
  /// Pruned to the COMBO_WINDOW each time we record.
  private landings: number[] = []
  private departures: number[] = []
  /// Tracks whether the last empty-radar check observed any active
  /// traffic. Combo fires on the false→true edge of "is empty"
  /// (i.e., active count just transitioned from >0 to 0).
  private hadActiveLastTick = false

  /// Returns a combo event if a 3-within-window has just fired,
  /// else null.
  recordLanding(simSec: number): ComboEvent | null {
    return this.recordSuccess(this.landings, simSec, 'tripleArrival')
  }

  recordDeparture(simSec: number): ComboEvent | null {
    return this.recordSuccess(this.departures, simSec, 'departureRush')
  }

  /// Empty-radar edge detection. Caller passes the current active
  /// aircraft count (after their per-tick state changes). Bonus
  /// fires once per radar-clearance — a sustained empty radar
  /// doesn't keep awarding.
  observeRadar(activeCount: number, levelStillRunning: boolean): ComboEvent | null {
    const empty = activeCount === 0
    const wasActive = this.hadActiveLastTick
    this.hadActiveLastTick = !empty
    // Only fire on the active→empty transition AND while the level
    // is still running. The end-of-level natural drain (every
    // aircraft handled) shouldn't earn a free 75 points.
    if (wasActive && empty && levelStillRunning) {
      return {
        kind: 'emptyRadar',
        description: 'Empty radar bonus',
        points: EMPTY_RADAR_BONUS,
      }
    }
    return null
  }

  private recordSuccess(
    arr: number[],
    simSec: number,
    kind: 'tripleArrival' | 'departureRush',
  ): ComboEvent | null {
    arr.push(simSec)
    // Drop any entry older than the window from the front. (Array
    // is monotonically increasing in simSec.)
    while (arr.length > 0 && simSec - arr[0]! > COMBO_WINDOW_SEC) {
      arr.shift()
    }
    if (arr.length >= 3) {
      // Consume the triple — clear the contributing entries so the
      // next combo needs three new successes.
      arr.length = 0
      return {
        kind,
        description:
          kind === 'tripleArrival' ? 'Triple arrival bonus' : 'Departure rush bonus',
        points:
          kind === 'tripleArrival' ? TRIPLE_ARRIVAL_BONUS : DEPARTURE_RUSH_BONUS,
      }
    }
    return null
  }
}
